import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import getOffset from '../../lib/getOffset';
import Modal from '../Modal/Modal';
import Gravity from '../../constants/Gravity';

const MENU_OFFSET = 26; // offset from arrow and to top of menu. TODO should instead be centered on the first menu item

/**
 * `PopupMenu` is a container for menu items that can be displayed in a fixed position or relative to another component by
 * setting the preferred *anchor* and *gravity* properties. If there is no room at the preferred gravity the closest gravity
 * with room enough will be selected. A glass pane is put on top of the rest of the UI and will hijack mouse clicks and mouse
 * wheel scrolling as long as the popup menu is visible.
 *
 * The popup menu will automatically closed when clicking on any of its items unless the generated DOM node of the element
 * has the *data-preventmenuclose* attribute set on it. The popup menu will check for this attribute on any parent of the
 * clicked element so you may set the attribute on any sub element in your custom component. The menu will automatically
 * close when the window is resized.
 *
 * If you need a button to toggle the visibility of the menu you should use the {@link Menu} component instead.
 *
 * @property {object|node} anchor Either an `object` with `{x, y}` or `{top, left}` values or a DOM element that the popup
 * menu will be placed relative to based on *gravity*.
 * @property {number} arrowSize The size of the menu arrow and affects how far the menu is displayed from the menu button.
 * Defaults to 10 pixels.
 * @property {node} children The content of your popup menu, usually {@link Button} or `a href` elements. If you want to add
 * your own custom UI be sure to set *customContent* to `true`.
 * @property {string} className The class name of this component. Will be appended to "core-popup-menu".
 * @property {boolean} customContent If set to `true`, the provided children will be inserted directly into the popup menu.
 * If not, an `ul` element will be created and all children will be inserted into `li` elements. Defaults to `false`.
 * @property {string} gravity Sets the preferred position of the menu relative to the anchor element.
 * See [`Gravity`](https://github.com/vimond/vimond-core-components/blob/master/src/constants/Gravity.js) for available
 * options. Defaults to `Gravity.SOUTH`.
 * @property {number} offset The number of pixels of extra spacing between the arrow/menu and the provided menu button.
 * Defaults to 0.
 * @property {function} onClose Callback that is called when popup menu closes. Your callback should remove the popup menu
 * from the parent container when this callback is called.
 * @property {boolean} useAnchorWidth If set to `true`, the popup will have the same width as the anchor element. By default,
 * the width of the popup is determined by its children. Should probably only be used when gravity is `SOUTH` or `NORTH.`.
 * Defaults to `false`.
 *
 * @see Menu
 */
export default class PopupMenu extends Component {

  static displayName = 'PopupMenu';

  static propTypes = {
    anchor: PropTypes.object,
    arrowSize: PropTypes.number,
    children: PropTypes.node,
    className: PropTypes.string,
    customContent: PropTypes.bool,
    gravity: PropTypes.string,
    offset: PropTypes.number,
    onClose: PropTypes.func,
    useAnchorWidth: PropTypes.bool,
  };

  static defaultProps = {
    arrowSize: 10,
    gravity: Gravity.SOUTHEAST,
    offset: 0,
  };

  static inside(top, left, width, height) {
    const { innerWidth, innerHeight } = window;

    return left >= 0 && top >= 0 && left + width <= innerWidth && top + height <= innerHeight;
  }

  static getArrowPosition(gravity, btnRect, arrowSize, offset) {
    let style;

    if (gravity === Gravity.NORTH) {
      style = {
        top: btnRect.top - arrowSize - offset,
        left: btnRect.left + btnRect.width / 2 - arrowSize
      };
    } else if (gravity === Gravity.SOUTH) {
      style = {
        top: btnRect.top + btnRect.height - arrowSize + offset,
        left: btnRect.left + btnRect.width / 2 - arrowSize
      };
    } else if (gravity.indexOf(Gravity.WEST) >= 0) {
      style = {
        top: btnRect.top + btnRect.height / 2 - arrowSize,
        left: btnRect.left - arrowSize - offset
      };
    } else {
      style = {
        top: btnRect.top + btnRect.height / 2 - arrowSize,
        left: btnRect.left + btnRect.width - arrowSize + offset
      };
    }
    style.borderWidth = `${arrowSize}px`;
    return style;
  }

  static getMenuPosition(gravity, rect, btnRect, arrowSize, offset) {
    let style;

    switch (gravity) {
      case Gravity.NORTHWEST:
        style = {
          top: btnRect.top + btnRect.height / 2 - rect.height + (arrowSize ? MENU_OFFSET : 0),
          left: btnRect.left - rect.width - arrowSize - offset
        };
        break;
      case Gravity.NORTH:
        style = {
          top: btnRect.top - rect.height - arrowSize - offset,
          left: btnRect.left + btnRect.width / 2 - rect.width / 2
        };
        break;
      case Gravity.NORTHEAST:
        style = {
          top: btnRect.top + btnRect.height / 2 - rect.height + (arrowSize ? MENU_OFFSET : 0),
          left: btnRect.left + btnRect.width + arrowSize + offset
        };
        break;
      case Gravity.EAST:
        style = {
          top: btnRect.top + btnRect.height / 2 - rect.height / 2,
          left: btnRect.left + btnRect.width + arrowSize + offset
        };
        break;
      case Gravity.SOUTH:
        style = {
          top: btnRect.top + btnRect.height + arrowSize + offset,
          left: btnRect.left + btnRect.width / 2 - rect.width / 2
        };
        break;
      case Gravity.SOUTHWEST:
        style = {
          top: btnRect.top + btnRect.height / 2 - (arrowSize ? MENU_OFFSET : 0),
          left: btnRect.left - rect.width - arrowSize - offset
        };
        break;
      case Gravity.WEST:
        style = {
          top: btnRect.top + btnRect.height / 2 - rect.height / 2,
          left: btnRect.left - rect.width - arrowSize - offset
        };
        break;
      case Gravity.SOUTHEAST:
      default:
        style = {
          top: btnRect.top + btnRect.height / 2 - (arrowSize ? MENU_OFFSET : 0),
          left: btnRect.left + btnRect.width + arrowSize + offset
        };
        break;
    }
    return style;
  }

  static forceInsideWindow(gravity, rect, btnRect, arrowSize, offset, useAnchorWidth) {
    const { innerWidth, innerHeight } = window;
    let { top, left } = rect;
    const { width, height } = rect;
    let newGravity = gravity;

    if (left + width > innerWidth || top + height > innerHeight) {
      if (left + width < innerWidth && gravity !== Gravity.NORTH && gravity !== Gravity.SOUTH) {
        top = innerHeight - height;
      }
      if (gravity.indexOf('east') > -1 && left + width > innerWidth) {
        newGravity = gravity.replace(/east/, 'west');
      } else if (gravity.indexOf('west') > -1 && left < 0) {
        newGravity = gravity.replace(/west/, 'east');
      }
      if (newGravity !== gravity) {
        const style = PopupMenu.getMenuPosition(newGravity, rect, btnRect, arrowSize, offset);

        top = Math.max(0, style.top);
        left = Math.max(0, style.left);
      }
      if (!PopupMenu.inside(top, left, width, height)) {
        const gravities = [Gravity.NORTHEAST, Gravity.EAST, Gravity.SOUTHEAST,
          Gravity.SOUTHWEST, Gravity.WEST, Gravity.NORTHWEST, Gravity.NORTH, Gravity.SOUTH];
        const index = gravities.indexOf(newGravity);
        const wheel = [];

        if (gravity === Gravity.SOUTH) {
          wheel.push(Gravity.NORTH);
        } else if (gravity === Gravity.NORTH) {
          wheel.push(Gravity.SOUTH);
        }
        wheel.push(gravities.splice(index + 1).concat(gravities.splice(0, index)));
        wheel.find(g => {
          const pos = PopupMenu.getMenuPosition(g, { top, left, width, height }, btnRect, arrowSize, offset);

          if (PopupMenu.inside(pos.top, pos.left, width, height)) {
            newGravity = g;
            top = pos.top;
            left = pos.left;
            return true;
          }
          return false;
        });
      }
      if (top + height > innerHeight) {
        top = innerHeight - height;
      }
      if (top < 0) {
        top = 0;
      }
      if (left + width > innerWidth) {
        left = innerWidth - width;
      }
      if (left < 0) {
        left = 0;
      }
    }
    return [{
      top,
      left: useAnchorWidth ? btnRect.left : left,
      width: useAnchorWidth ? btnRect.width : undefined,
    }, newGravity];
  }

  constructor(props) {
    super(props);
    this.state = {
      style: {},
      gravity: props.gravity,
      arrowStyle: {}
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.calculatePositionsAndSetState();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onClick = e => {
    if (!this.isPreventMenuClose(e.target)) {
      this.onClose(e, e.target.localName !== 'a');
    }
  };

  onClose = (e, preventEvent = true) => {
    if (this.props.onClose) {
      this.props.onClose(e, preventEvent);
    }
  };

  onResize = e => this.props.onClose && this.props.onClose(e);

  calculatePositionsAndSetState = () => {
    const { arrowSize, anchor, offset, useAnchorWidth } = this.props;
    let { gravity } = this.props;
    let style;
    let arrowStyle;
    let btnRect;

    if (anchor && this.refItems && window) {
      const rect = { width: this.refItems.clientWidth, height: this.refItems.clientHeight };

      if (anchor.nodeType) {
        btnRect = getOffset(anchor);
      } else if ((anchor.top !== undefined && anchor.left !== undefined) || (anchor.x !== undefined && anchor.y !== undefined)) {
        btnRect = {
          top: anchor.y || anchor.top,
          left: anchor.x || anchor.left,
          width: 0,
          height: 0,
        };
      }
      // TODO: clean up forcing within visible area
      style = PopupMenu.getMenuPosition(gravity, rect, btnRect, arrowSize, offset);
      [style, gravity] = PopupMenu.forceInsideWindow(gravity, {
        top: style.top,
        left: style.left,
        width: rect.width,
        height: rect.height
      }, btnRect, arrowSize, offset, useAnchorWidth);
      arrowStyle = PopupMenu.getArrowPosition(gravity, btnRect, arrowSize, offset);
      if (arrowStyle.left > style.left && arrowStyle.left < style.left + rect.width &&
        arrowStyle.top > style.top && arrowStyle.top < style.top + rect.height) {
        arrowStyle.display = 'none';
      }
    }
    if (style) {
      this.setState({
        style,
        arrowStyle,
        gravity
      });
    }
  };

  isPreventMenuClose = element => {
    if (element && element !== this.refItems && element.getAttribute) {
      if (element.getAttribute('data-preventmenuclose')) {
        return true;
      }
      return this.isPreventMenuClose(element.parentNode);
    }
    return false;
  };

  ref = c => (this.refItems = c);

  render() {
    const { arrowSize, children, className, customContent } = this.props;
    const { gravity, arrowStyle, style } = this.state;
    let arrow;
    let content;

    if (arrowSize > 0 && children) {
      arrow = <div className={cx('core-popup-menu-arrow', gravity)} style={arrowStyle} data-testid="popup-menu-arrow"/>;
    }
    if (customContent) {
      content = <div ref={this.ref} className={cx('custom', className && `${className}-custom`)} style={style}>{children}</div>;
    } else {
      content = (
        <ul ref={this.ref} style={style} className="popup-list">
          {[].concat(children)
            .filter(item => item)
            .map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      );
    }
    return (
      <Modal className="core-popup-menu-wrapper" onClick={this.onClick} onClose={this.onClose}>
        <div className={cx('core-popup-menu', className)}>
          {content}
          {arrow}
        </div>
      </Modal>
    );
  }

}
